
import React, { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import OrderDetails from "./form/OrderDetails";
import MessageField from "./form/MessageField";
import StatusSelect from "./form/StatusSelect";
import PriorityCheckbox from "./form/PriorityCheckbox";
import NotificationRecipientSelect from "./NotificationRecipientSelect";
import FormActions from "./form/FormActions";
import { NotificationProductData } from "@/types/notifications";

interface CreateNotificationFormProps {
  notification: {
    type: string;
    voNumber: string;
    exactOrderNumber: string;
    message: string;
    priority: string | null;
    userId: string;
    userName: string;
    status: string;
    recipients: {
      internal: {
        group1: boolean;
        group2: boolean;
      };
      external: string[];
    };
    products?: NotificationProductData[];
  };
  isPending: boolean;
  isAnalyzing: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onSelectChange: (name: string, value: string) => void;
  onCheckboxChange: (name: string, value: string | boolean) => void;
  onInternalCheckboxChange: (group: 'group1' | 'group2') => void;
  onToggleExternalRecipient: (recipient: string) => void;
  onProductsChange: (products: NotificationProductData[]) => void;
  onAIAnalysisComplete: (data: { formattedText: string; voNumber: string | null }) => void;
  setIsAnalyzing: (isAnalyzing: boolean) => void;
  onCancel: () => void;
  onSubmit: (e: React.FormEvent) => void;
}

const CreateNotificationForm = ({
  notification,
  isPending,
  isAnalyzing,
  onInputChange,
  onSelectChange,
  onCheckboxChange,
  onInternalCheckboxChange,
  onToggleExternalRecipient,
  onProductsChange,
  onAIAnalysisComplete,
  setIsAnalyzing,
  onCancel,
  onSubmit
}: CreateNotificationFormProps) => {
  const isMobile = useIsMobile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Check if all required fields are filled
  const isFormComplete = 
    notification.type !== '' && 
    notification.voNumber.trim() !== '' && 
    notification.message.trim() !== '' &&
    notification.status !== '' &&
    (!notification.recipients.internal.group1 || validateProducts(notification.products || []));
    
  // Validate products if Inkoop Goes is checked
  function validateProducts(products: NotificationProductData[]): boolean {
    if (notification.recipients.internal.group1) {
      if (!products.length) return false;
      
      return products.every(product => 
        product.product_name.trim() !== '' && 
        product.product_sku.trim() !== '' && 
        product.product_bc.trim() !== '' &&
        product.product_quantity > 0
      );
    }
    return true;
  }
  
  // Handle submit with prevention of movement
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Set submitting state to true to trigger CSS fixes
    setIsSubmitting(true);
    
    // If on mobile, add a class to the body to prevent movement
    if (isMobile) {
      document.body.classList.add('form-submitting');
    }
    
    // Call the original onSubmit handler
    onSubmit(e);
  };

  return (
    <form 
      onSubmit={handleSubmit} 
      className="space-y-4" 
      data-submitting={isSubmitting ? "true" : "false"}
      style={isMobile ? {
        position: 'static',
        transform: 'none',
        touchAction: 'auto',
        pointerEvents: 'auto'
      } : undefined}
    >
      <OrderDetails
        type={notification.type}
        voNumber={notification.voNumber}
        exactOrderNumber={notification.exactOrderNumber}
        onTypeChange={(value) => onSelectChange('type', value)}
        onInputChange={onInputChange}
      />
      
      <NotificationRecipientSelect
        internalRecipients={notification.recipients.internal}
        products={notification.products || []}
        onInternalCheckboxChange={onInternalCheckboxChange}
        onProductsChange={onProductsChange}
      />
      
      <MessageField
        message={notification.message}
        isAnalyzing={isAnalyzing}
        setIsAnalyzing={setIsAnalyzing}
        onMessageChange={onInputChange}
        onAIAnalysisComplete={onAIAnalysisComplete}
      />
      
      <StatusSelect 
        status={notification.status} 
        onStatusChange={(value) => onSelectChange('status', value)} 
      />
      
      <PriorityCheckbox 
        isPriorityHigh={notification.priority === 'hoog'}
        onPriorityChange={(checked) => {
          onCheckboxChange('priority', checked === true ? 'hoog' : 'normaal');
        }}
      />
      
      <FormActions
        isPending={isPending}
        isFormComplete={isFormComplete}
        isMobile={isMobile}
        onCancel={onCancel}
      />
    </form>
  );
};

export default CreateNotificationForm;
